//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useCallback } from 'react';
import { useState, useEffect, useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
// import { Grid } from '@material-ui/core';
import { useParams, useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { PATH_HOME } from '../system/constants';
import { LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import { loadProfileByCustomName, loadProfileByEmployerId } from '../common/services/empMyProfileServices';
import { loadEmployerData } from '../common/services/appServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import TopNav from '../topNav/TopNav';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import BrandPage from './subComponents/BrandPage';
import AfitdEditDialog from '../common/elemental/AfitdEditDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import BrandPageUnavailable from './subComponents/BrandPageUnavailable';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    pageContainer: {
        backgroundColor: '#FAFAFC',
        overflow: 'hidden',
        height: '100%',
        minHeight: '100%',
        flexWrap: 'unset',
    },
    pageHeader: { maxHeight: '81px' },
    pageContent: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingLeft: '64px',
        paddingRight: '64px',
    },
    jobCenterLeft: { height: '100%', paddingTop: '25px' },
    jobCenterRight: {
        height: '100%',
        overflowY: 'hidden',
        flexWrap: 'unset',
        paddingTop: '35px',
        minHeight: '600px',
    },
    optionMenuContainer: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        marginTop: '-45px',
    },
    infoButton: {
        marginRight: '5px',
        fontSize: '15px',
        fontWeight: 'bold',
        color: 'rgba(17, 30, 20, 0.4)',
    },
    jobButton: {
        marginLeft: '5px',
        fontSize: '15px',
        fontWeight: 'bold',
        color: 'rgba(17, 30, 20, 0.4)',
    },
    badge: {
        marginLeft: '20px',
        marginRight: '10px',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        // brandEmployerId,
        // brandCustomName,
        employer,
        // setEmployer,
        handleChangeMenu,
        selectedMenu,
        brandPageDialogOpen,
        // setBrandPageDialogOpen,
        handleCloseBrandPageDialog,
    } = params;

    const defns = {
        pageContainer: { container: true, direction: 'column' },
        pageHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        companyProfile: {
            container: true,
            style: {
                display: selectedMenu !== 'info' && 'none',
            },
        },
        jobProfile: {
            container: true,
            style: {
                display: selectedMenu !== 'job' && 'none',
            },
        },
        companyInfo: { container: true, lg: 4 },
        companyBio: { container: true, lg: 8 },
        pageContent: { item: true, container: true },
        contentContainer: { item: true, container: true, direction: 'column' },
        infoButton: {
            onClick: () => handleChangeMenu('info'),
            style: {
                backgroundColor: selectedMenu !== 'info' && '#00000000',
                color: selectedMenu === 'info' && 'black',
            },
        },
        jobButton: {
            onClick: () => handleChangeMenu('job'),
            style: {
                backgroundColor: selectedMenu !== 'job' && '#00000000',
                color: selectedMenu === 'job' && 'black',
            },
        },
        brandPageDialog: {
            fullScreen: true,
            open: brandPageDialogOpen,
            // isDirty,
            onClose: handleCloseBrandPageDialog,
            hideActions: true,
        },
        brandPage: {
            employer,
        },
        brandPageNotAvailable: {
            employer,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const EmpBrandPageLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const { brandEmployerId, brandCustomName } = useParams();
    const history = useHistory();

    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    const context = useContext(LayoutContext);
    // const { setSnackbarMessage, setSnackbarErrorMessage } = context;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { isShowJobs } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [profileData, setProfileData] = useState(null);
    const [employer, setEmployer] = useState(null);
    const [selectedMenu, setSelectedMenu] = useState('info');
    const [brandPageDialogOpen, setBrandPageDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // viewing brand page, fetch employer data first
    useEffect(() => {
        if (brandEmployerId) {
            // a work-around to delay the firebase call so the app can call firebase.initializeApp() first.
            setTimeout(() => {
                loadEmployerData({
                    employerId: brandEmployerId,
                    setEmployer,
                });
            }, 200);
        }
    }, [brandEmployerId]);

    // fetch profile data when employer data is loaded
    useEffect(() => {
        if (brandEmployerId && employer) {
            // a work-around to delay the firebase call so the app can call firebase.initializeApp() first.
            setTimeout(() => {
                loadProfileByEmployerId({
                    employerId: brandEmployerId,
                    setData: setProfileData,
                });
            }, 200);
        }
    }, [brandEmployerId, employer]);

    // viewing brand page with custom name, fetch profile data first
    useEffect(() => {
        if (brandCustomName) {
            // a work-around to delay the firebase call so the app can call firebase.initializeApp() first.
            setTimeout(() => {
                loadProfileByCustomName({
                    customName: brandCustomName,
                    setData: setProfileData,
                    setEmployer,
                });
            }, 200);
        }
    }, [brandCustomName]);

    // when data are ready, open dialog
    useEffect(() => {
        if (profileData) {
            setBrandPageDialogOpen(true);
        }
    }, [profileData]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isPublic = _get(profileData, 'isPublic'); // do not set default value as we want the undefined state in order to show blank

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeMenu = item => {
        setSelectedMenu(item);
    };

    const handleCloseBrandPageDialog = useCallback(() => {
        history.push(`${PATH_HOME}`);
        setBrandPageDialogOpen(false);
        setProfileData(null);
    }, [history]);

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        brandEmployerId,
        brandCustomName,
        employer,
        setEmployer,
        handleChangeMenu,
        selectedMenu,
        brandPageDialogOpen,
        setBrandPageDialogOpen,
        handleCloseBrandPageDialog,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context, profileData }}>
            {/* <Grid {...defns.pageContainer}>
                <Grid {...defns.pageHeader}>
                    <TopNav />
                </Grid>
                <Grid {...defns.pageContent}>
                    <Grid {...defns.contentContainer}>
                        <BrandPage />
                    </Grid>
                </Grid>
            </Grid> */}
            {brandPageDialogOpen && (
                <AfitdEditDialog {...defns.brandPageDialog}>
                    {isPublic === true && <BrandPage {...defns.brandPage} />}
                    {isPublic === false && <BrandPageUnavailable {...defns.brandPageNotAvailable} />}
                </AfitdEditDialog>
            )}
        </LayoutContext.Provider>
    );
};

export default EmpBrandPageLayout;
